import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CountriesService } from '../../../shared/services/countries.service';
import { MediaAssetDto, MediaService } from '../../../shared/services/media.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminPageHeaderComponent,
  AdminSkeletonComponent,
} from '../../../shared/admin';
import { PermissionService } from '../../permissions/permission.service';
import {
  CountryFormModel,
  countryToFormModel,
  emptyCountryForm,
  formModelToPayload,
  MAX_WHY_CARDS,
  newWhyCardKey,
  whyCardsFromDto,
  whyCardsToPayload,
} from './country-form.utils';

@Component({
  selector: 'app-country-form-page',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminButtonComponent,
    AdminCardComponent,
    AdminSkeletonComponent,
  ],
  templateUrl: './country-form-page.component.html',
})
export class CountryFormPageComponent {
  private readonly service = inject(CountriesService);
  private readonly media = inject(MediaService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.countries', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.countries', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.countries', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.countries', 'edit'));
  readonly showUpload = computed(() => this.permissions.canShow('website.countries', 'upload'));
  readonly canUpload = computed(() => this.permissions.canExecute('website.countries', 'upload'));
  readonly showPublish = computed(() => this.permissions.canShow('website.countries', 'publish'));
  readonly canPublish = computed(() => this.permissions.canExecute('website.countries', 'publish'));
  readonly canSave = computed(() => (this.isEdit() ? this.canEdit() : this.canCreate()));
  readonly showSave = computed(() => (this.isEdit() ? this.showEdit() : this.showCreate()));

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly loadError = signal<string | null>(null);
  readonly heroPreviewUrl = signal<string | null>(null);
  readonly heroUploading = signal(false);
  readonly heroUploadMessage = signal<string | null>(null);
  readonly saveMessage = signal<string | null>(null);
  readonly whyCardPreviewUrls = signal<Record<string, string>>({});
  readonly whyCardUploadingKey = signal<string | null>(null);
  readonly whyCardMessage = signal<string | null>(null);

  readonly maxWhyCards = MAX_WHY_CARDS;
  heroFile: File | null = null;
  private whyCardFiles: Record<string, File | null> = {};

  readonly countryId = computed(() => {
    const id = this.route.snapshot.paramMap.get('id');
    return id && id !== 'new' ? id : null;
  });

  readonly isEdit = computed(() => !!this.countryId());

  form: CountryFormModel = emptyCountryForm();

  constructor() {
    const id = this.countryId();
    if (!id) {
      this.loading.set(false);
      return;
    }
    this.service.getPlatformCountry(id).subscribe({
      next: (country) => {
        this.form = countryToFormModel(country);
        this.loadHeroPreview(this.form.heroMediaAssetId);
        this.loadWhyCardPreviews();
        this.loading.set(false);
      },
      error: () => {
        this.loadError.set('Country not found.');
        this.loading.set(false);
      },
    });
  }

  onHeroFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.heroFile = input.files?.[0] ?? null;
    this.heroUploadMessage.set(null);
  }

  uploadHero(): void {
    if (!this.heroFile) return;
    this.heroUploading.set(true);
    this.heroUploadMessage.set(null);
    const altText = this.form.name
      ? `Hero background – ${this.form.name}`
      : 'Country hero background';
    this.media.upload(this.heroFile, { altText }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.heroUploading.set(false);
          this.heroUploadMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        this.form.heroMediaAssetId = assetId;
        this.heroPreviewUrl.set(asset.url);
        this.heroFile = null;
        this.persistHeroMedia(assetId, () => {
          this.heroUploading.set(false);
          this.heroUploadMessage.set(
            this.countryId()
              ? 'Hero image saved. It will appear on the public country page.'
              : 'Image uploaded. Click Save country to store it with this country.',
          );
        }, (msg) => {
          this.heroUploading.set(false);
          this.heroUploadMessage.set(`${msg} Re-upload or click Save country to retry.`);
        });
      },
      error: (err) => {
        this.heroUploading.set(false);
        this.heroUploadMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  clearHero(): void {
    this.form.heroMediaAssetId = '';
    this.heroPreviewUrl.set(null);
    this.heroFile = null;
    this.persistHeroMedia('', () => {
      this.heroUploadMessage.set(
        this.countryId()
          ? 'Hero image removed from the public country page.'
          : 'Hero image cleared. Save country to confirm.',
      );
    }, (msg) => {
      this.heroUploadMessage.set(msg);
    });
  }

  cancel(): void {
    void this.router.navigate(['/platform/countries']);
  }

  canAddWhyCard(): boolean {
    return this.form.whyCards.length < this.maxWhyCards;
  }

  addWhyCard(): void {
    if (!this.canAddWhyCard()) return;
    this.form.whyCards = [
      ...this.form.whyCards,
      { key: newWhyCardKey(), title: '', description: '', imageMediaAssetId: '' },
    ];
  }

  removeWhyCard(key: string): void {
    this.form.whyCards = this.form.whyCards.filter((c) => c.key !== key);
    delete this.whyCardFiles[key];
    this.whyCardPreviewUrls.update((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  whyCardPreview(key: string): string | null {
    return this.whyCardPreviewUrls()[key] ?? null;
  }

  hasWhyCardFile(key: string): boolean {
    return !!this.whyCardFiles[key];
  }

  onWhyCardFile(key: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.whyCardFiles[key] = input.files?.[0] ?? null;
    this.whyCardMessage.set(null);
  }

  uploadWhyCardImage(key: string): void {
    const file = this.whyCardFiles[key];
    const card = this.form.whyCards.find((c) => c.key === key);
    if (!file || !card) return;
    this.whyCardUploadingKey.set(key);
    this.whyCardMessage.set(null);
    const altText = card.title
      ? `Why study – ${card.title}`
      : `Why study – ${this.form.name || 'country'}`;
    this.media.upload(file, { altText }).subscribe({
      next: (asset) => {
        const assetId = this.mediaAssetId(asset);
        if (!assetId) {
          this.whyCardUploadingKey.set(null);
          this.whyCardMessage.set('Upload succeeded but no asset id was returned.');
          return;
        }
        card.imageMediaAssetId = assetId;
        const previewUrl = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
        this.whyCardPreviewUrls.update((prev) => ({ ...prev, [key]: previewUrl }));
        this.whyCardFiles[key] = null;
        this.persistWhyCards(() => {
          this.whyCardUploadingKey.set(null);
          this.whyCardMessage.set(
            this.countryId()
              ? 'Card image saved.'
              : 'Image uploaded. Click Save country to store it.',
          );
        }, (msg) => {
          this.whyCardUploadingKey.set(null);
          this.whyCardMessage.set(msg);
        });
      },
      error: (err) => {
        this.whyCardUploadingKey.set(null);
        this.whyCardMessage.set(err?.error?.message ?? 'Upload failed.');
      },
    });
  }

  clearWhyCardImage(key: string): void {
    const card = this.form.whyCards.find((c) => c.key === key);
    if (!card) return;
    card.imageMediaAssetId = '';
    delete this.whyCardFiles[key];
    this.whyCardPreviewUrls.update((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    this.persistWhyCards(
      () => this.whyCardMessage.set('Card image removed.'),
      (msg) => this.whyCardMessage.set(msg),
    );
  }

  save(): void {
    let payload;
    try {
      payload = formModelToPayload(this.form);
    } catch (err) {
      const field = err instanceof Error ? err.message : null;
      this.error.set(
        field
          ? `Invalid JSON in “${field}”. Fix the JSON syntax and try again.`
          : 'Invalid JSON in one of the marketing text areas.',
      );
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.saveMessage.set(null);
    const id = this.countryId();
    const op$ = id ? this.service.update(id, payload) : this.service.create(payload);
    op$.subscribe({
      next: (saved) => {
        this.saving.set(false);
        this.error.set(null);
        if (!saved) return;
        const previousWhy = this.form.whyCards;
        this.form = countryToFormModel(saved);
        this.form.whyCards = whyCardsFromDto(saved.whyCards, previousWhy);
        this.loadHeroPreview(this.form.heroMediaAssetId);
        this.loadWhyCardPreviews();
        this.saveMessage.set('Country saved.');
        if (id) {
          return;
        }
        void this.router.navigate(['/platform/countries', saved._id, 'edit'], { replaceUrl: true });
      },
      error: (err) => {
        this.saving.set(false);
        const body = err?.error as { message?: string; errors?: string[] } | undefined;
        const detail = Array.isArray(body?.errors)
          ? body.errors.join('; ')
          : body?.message;
        this.error.set(detail ?? 'Save failed.');
      },
    });
  }

  private mediaAssetId(asset: MediaAssetDto): string {
    const raw = asset._id ?? (asset as MediaAssetDto & { id?: string }).id;
    return raw != null ? String(raw) : '';
  }

  private persistHeroMedia(
    assetId: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ): void {
    const countryId = this.countryId();
    if (!countryId) {
      onSuccess();
      return;
    }
    this.service.update(countryId, { heroMediaAssetId: assetId.trim() }).subscribe({
      next: (country) => {
        this.form = countryToFormModel(country);
        this.loadHeroPreview(this.form.heroMediaAssetId);
        onSuccess();
      },
      error: (err) => {
        const body = err?.error as { message?: string } | undefined;
        onError(body?.message ?? 'Could not save hero image reference.');
      },
    });
  }

  private loadHeroPreview(assetId: string): void {
    const id = assetId.trim();
    if (!id) {
      this.heroPreviewUrl.set(null);
      return;
    }
    this.media.getById(id).subscribe({
      next: (asset) => this.heroPreviewUrl.set(asset.url),
      error: () => this.heroPreviewUrl.set(null),
    });
  }

  private persistWhyCards(onSuccess: () => void, onError: (message: string) => void): void {
    const countryId = this.countryId();
    if (!countryId) {
      onSuccess();
      return;
    }
    this.service.update(countryId, { whyCards: whyCardsToPayload(this.form.whyCards) }).subscribe({
      next: (country) => {
        const previous = this.form.whyCards;
        this.form = countryToFormModel(country);
        this.form.whyCards = whyCardsFromDto(country.whyCards, previous);
        this.loadWhyCardPreviews(this.whyCardPreviewUrls());
        onSuccess();
      },
      error: (err) => {
        const body = err?.error as { message?: string } | undefined;
        onError(body?.message ?? 'Could not save why study cards.');
      },
    });
  }

  private loadWhyCardPreviews(existing: Record<string, string> = {}): void {
    const previews: Record<string, string> = { ...existing };
    let pending = 0;
    for (const card of this.form.whyCards) {
      const id = card.imageMediaAssetId?.trim();
      if (!id) continue;
      if (previews[card.key]) continue;
      pending += 1;
      this.media.getById(id).subscribe({
        next: (asset) => {
          previews[card.key] = (asset as MediaAssetDto & { cdnUrl?: string }).cdnUrl || asset.url;
          pending -= 1;
          if (pending === 0) this.whyCardPreviewUrls.set({ ...previews });
        },
        error: () => {
          pending -= 1;
          if (pending === 0) this.whyCardPreviewUrls.set({ ...previews });
        },
      });
    }
    if (pending === 0) this.whyCardPreviewUrls.set(previews);
  }
}
